import * as ko from 'knockout';
import { onShowTogglable, TriggerEvent } from '../../../../../src/modules/core/togglables';
import { debounce } from '../../../../utils/debounce';
import { addDelegatedEventListener } from '../../../../utils/delegate';
import type { InversifyInjector } from '@paperbits/common/injection';
import type { SearchClient } from '../../../../search/client/searchClient';
import { SearchSuggetionRuntimeViewModel } from './searchSuggestionRuntimeViewModel';
import { type MenuRuntimeModel, MenuRuntimeModelBuilder } from './menuExchangeModel';

declare global {
    interface Window { dataLayer: unknown[]; }
}

export class MenuRuntime extends HTMLElement {

    private mobileMenuToggle: HTMLElement;
    private firstLevel: HTMLElement;
    private firstLevelOpen: Set<HTMLElement> = new Set();
    private search: HTMLElement;
    private suggestionRuntimeViewModel: SearchSuggetionRuntimeViewModel;
    private suggestionWrapperElements: () => NodeListOf<HTMLElement>;
    private runtimeModel: MenuRuntimeModel;
    private mobileMQ = window.matchMedia('(max-width: 1023.9px)');

    constructor() {
        super();
        this.suggestionWrapperElements = (): NodeListOf<HTMLElement> => this.querySelectorAll<HTMLElement>('.search-suggestions');
        this.suggestionRuntimeViewModel = new SearchSuggetionRuntimeViewModel();
    }

    static get observedAttributes(): string[] {
        return ['params'];
    }

    public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (name === 'params') {
            if (oldValue !== newValue && newValue?.length) {
                try {
                    this.runtimeModel = MenuRuntimeModelBuilder.fromSerialized(newValue, true);
                } catch {
                    console.error('Unable to parse "params" for "cards-wall".');
                }
            }
        }
    }

    private updateOpenStatus = (): void => {
        document.body.classList.toggle('overflow-hidden', this.firstLevelOpen.size > 0);
    }

    private toggleMobileMenu = (event: MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        onShowTogglable(this.mobileMenuToggle, this.firstLevel);
    };

    private toggleFirstLevel = (event: CustomEvent): void => {
        if (event.type === 'togglable-open') {
            this.firstLevelOpen.add(event.detail.target);
        } else if (event.type === 'togglable-close') {
            this.firstLevelOpen.delete(event.detail.target);
        }
        setTimeout(this.updateOpenStatus);
    };

    private toggleTransition = (event: TransitionEvent): void => {
        const target = event.target as HTMLElement;
        if (event.propertyName === 'max-height' && target.clientHeight === 0) {
            this.firstLevelOpen.delete(target);
            setTimeout(this.updateOpenStatus);
        }
    };

    private toggleSearch = (event: Event): void => {
        const open = event.type === 'togglable-open';
        for (const el of this.querySelectorAll<HTMLElement>('nav, .header-top, .sm-collapsed')) {
            el.style.opacity = open ? '0' : '';
            el.style.pointerEvents = open ? 'none' : '';
        }
        if (open) {
            (this.querySelector('#search__input') as HTMLInputElement).focus()
        }

        window.dataLayer.push({
            event: 'internal_search',
            eventCategory: 'site_search',
            eventAction: `${open ? 'open' : 'close'}_internal_search`,
            eventLabel: `language_${this.runtimeModel.siteLanguage}`
        });
    }

    private submitForm = (input: HTMLInputElement): void => {
        if (input.value.length) {
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: 'click_submit_search',
                eventLabel: `language_${this.runtimeModel.locale}`,
                keyword: input.value || 'null'
            });
            input.closest('form').submit();
        }
    }

    private keyboardLanguageDesktopNavigation = (e: KeyboardEvent): void => {
        e.preventDefault();
        const target = e.target as HTMLElement;
        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowRight':
                break;
            case 'ArrowUp': {
                target.tabIndex = -1;
                const interactive = Array.from(target.closest('.dropdown').querySelectorAll<HTMLElement>('a, button'))
                const index = interactive.indexOf(target)
                let prev = interactive[index - 1];
                if (!prev) { // wrap if first
                    prev = interactive[interactive.length - 1]
                }
                prev.tabIndex = 0;
                prev.focus();
                break;
            }
            case 'ArrowDown': {
                const submenu = target.closest('.dropdown');
                if (target.ariaExpanded === "false") {
                    target.click()
                } else if (submenu) {
                    target.tabIndex = -1;
                    const interactive = Array.from(submenu.querySelectorAll<HTMLElement>('a, button'))
                    const index = interactive.indexOf(target)
                    let next = interactive[index + 1];
                    if (!next) { // wrap if last
                        next = interactive[0]
                    }
                    next.tabIndex = 0;
                    next.focus();
                }
                break;
            }
            case 'Enter': {
                if (!target.ariaDisabled) { target.click() }
                break;
            }
            case 'Tab': {
                // close dropdown
                target.closest('.header-top').querySelector<HTMLElement>('[aria-expanded="true"]')?.click()
                if (e.shiftKey) {
                    document.body.focus() // TODO it should land on landmark link when implemented
                    // document.querySelector<HTMLElement>('#bypass-block-footer-anchor').focus()
                } else {
                    document.querySelector<HTMLElement>('.logo a').focus()
                }
                break;
            }
            case 'Escape': {
                // if target is in submenu close it
                const submenu = target.closest('.dropdown')
                if (submenu) {
                    const parent = submenu.previousElementSibling as HTMLElement
                    parent.click(); // close dropdown
                    parent.focus();
                }
                break;
            }
        }
    }

    private keyboardMainNavigation = (e: KeyboardEvent): void => {
        e.preventDefault();
        const target = e.target as HTMLElement;
        if (this.mobileMQ.matches) {
            switch (e.key) {
                case 'ArrowLeft':
                case 'ArrowRight':
                    break;
                case 'ArrowUp': {
                    let interactive: HTMLElement[];
                    const submenu = target.closest('.dropdown')
                    if (submenu) {
                        interactive = Array.from(submenu.querySelectorAll<HTMLElement>('[data-dismiss], [role="menuitem"]'))
                    } else {
                        // also include mobile language selector
                        interactive = Array.from(this.firstLevel.querySelectorAll<HTMLElement>('.first-level > [role="menuitem"], .mobile-service-menu > nav > button'))
                    }
                    target.tabIndex = -1;
                    const index: number = interactive.indexOf(target);
                    let prev: HTMLElement = interactive[index - 1];
                    if (index - 1 === -1) { // wrap if first
                        prev = interactive[interactive.length - 1]
                    }
                    prev.tabIndex = 0;
                    prev.focus();
                    break;
                }
                case 'ArrowDown': {
                    let interactive: HTMLElement[];
                    const submenu = target.closest('.dropdown')
                    if (submenu) {
                        interactive = Array.from(submenu.querySelectorAll<HTMLElement>('[data-dismiss], [role="menuitem"]'))
                    } else {
                        // also include mobile language selector
                        interactive = Array.from(this.firstLevel.querySelectorAll<HTMLElement>('.first-level > [role="menuitem"], .mobile-service-menu > nav > button'))
                    }
                    target.tabIndex = -1;
                    const index: number = interactive.indexOf(target);
                    let next: HTMLElement = interactive[index + 1];
                    if (index + 1 === interactive.length) {
                        next = interactive[0];  // wrap if last
                    }
                    next.tabIndex = 0;
                    next.focus();
                    break;
                }
                case 'Enter': {
                    if (!target.ariaDisabled) {
                        target.click();
                        // give focus to parent if it's in submenu
                        const submenu = target.closest('.dropdown')
                        if (submenu) {
                            (submenu.previousElementSibling as HTMLElement)?.focus()
                        }
                    }
                    break;
                }
                case 'Tab': {
                    this.firstLevel.querySelector<HTMLElement>('[aria-expanded="true"]')?.click() // close expanded
                    this.mobileMenuToggle.click() // close main dropdown too
                    if (e.shiftKey) {
                        document.querySelector<HTMLElement>('#search-toggle').focus()
                    } else {
                        document.querySelector<HTMLElement>('main a, main button')?.focus() // TODO test
                    }
                    break;
                }
                case 'Escape': {
                    // if target is in submenu close it
                    const submenu = target.closest('.dropdown')
                    if (submenu) {
                        const parent = submenu.previousElementSibling as HTMLElement
                        parent.click(); // close dropdown
                        parent.focus();
                    } else {
                        this.mobileMenuToggle.click() // close main dropdown
                        this.mobileMenuToggle.focus()
                    }
                    break;
                }
            }
        } else {
            switch (e.key) {
                case 'ArrowLeft': {
                    const interactive = Array.from(this.firstLevel.querySelectorAll<HTMLElement>('.first-level > [role="menuitem"]'))
                    let index: number;
                    let open: boolean;
                    // if target is in submenu move through menubar
                    if (target.closest('.dropdown')) {
                        index = interactive.findIndex(i => i.tabIndex === 0)
                        open = true
                        interactive[index].click() // close dropdown
                        interactive[index].tabIndex = -1
                    } else {
                        target.tabIndex = -1;
                        index = interactive.indexOf(target)
                        open = target.ariaExpanded === "true" // first level element opened
                        if (open) {
                            target.click()
                        }
                    }
                    let prev: HTMLElement = interactive[index - 1];
                    if (index - 1 === -1) {  // wrap if first
                        prev = interactive[interactive.length - 1]
                    }
                    if (open && prev.ariaExpanded === "false") { // open next dropdown without focus within
                        onShowTogglable(prev, prev.nextElementSibling as HTMLElement, true, false)
                    }
                    prev.tabIndex = 0;
                    prev.focus();
                    break;
                }
                case 'ArrowRight': {
                    const interactive = Array.from(this.firstLevel.querySelectorAll<HTMLElement>('.first-level > [role="menuitem"]'))
                    let index: number;
                    let open: boolean;
                    // if target is in submenu move through menubar
                    if (target.closest('.dropdown')) {
                        index = interactive.findIndex(i => i.tabIndex === 0)
                        open = true
                        interactive[index].click() // close dropdown
                        interactive[index].tabIndex = -1
                    } else {
                        target.tabIndex = -1;
                        index = interactive.indexOf(target)
                        open = target.ariaExpanded === "true" // first level element opened
                        if (open) {
                            target.click()
                        }
                    }
                    let next: HTMLElement = interactive[index + 1];
                    if (index + 1 === interactive.length) {  // wrap if last
                        next = interactive[0];
                    }
                    if (open && next.ariaExpanded === "false") { // open next dropdown without focus within
                        onShowTogglable(next, next.nextElementSibling as HTMLElement, true, false)
                    }
                    next.tabIndex = 0;
                    next.focus();
                    break;
                }
                case 'ArrowUp': {
                    target.tabIndex = -1;
                    const interactive = Array.from(target.closest('.dropdown').querySelectorAll<HTMLElement>('a, button:not(.all-displayed button):not([data-dismiss])'))
                    const index = interactive.indexOf(target)
                    let prev = interactive[index - 1];
                    if (!prev) { // wrap if first
                        prev = interactive[interactive.length - 1]
                    }
                    prev.tabIndex = 0;
                    prev.focus();
                    break;
                }
                case 'ArrowDown': {
                    const submenu = target.closest('.dropdown');
                    if (target.ariaExpanded === "false") {
                        target.click()
                    } else if (submenu) {
                        target.tabIndex = -1;
                        const interactive = Array.from(submenu.querySelectorAll<HTMLElement>('a, button:not(.all-displayed button):not([data-dismiss])'))
                        const index = interactive.indexOf(target)
                        let next = interactive[index + 1];
                        if (!next) { // wrap if last
                            next = interactive[0]
                        }
                        next.tabIndex = 0;
                        next.focus();
                    } else {
                        target.nextElementSibling.querySelector<HTMLElement>('a, button:not([data-dismiss])')?.focus()
                    }
                    break;
                }
                case 'Enter': {
                    if (!target.ariaDisabled) {
                        target.click()
                    }
                    break;
                }
                case 'Tab': {
                    // close dropdown
                    this.firstLevel.querySelector<HTMLElement>('[aria-expanded="true"]')?.click()
                    if (e.shiftKey) {
                        document.querySelector<HTMLElement>('.logo a').focus()
                    } else {
                        document.querySelector<HTMLElement>('#search-toggle').focus()
                    }
                    break;
                }
                case 'Escape': {
                    // if target is in submenu close it
                    const submenu = target.closest('.dropdown')
                    if (submenu) {
                        const parent = submenu.previousElementSibling as HTMLElement
                        parent.click(); // close dropdown
                        parent.focus();
                    }
                    break;
                }
            }
        }
    }

    public async connectedCallback(): Promise<void> {
        setTimeout(() => {
            window.dataLayer = window.dataLayer || [];

            this.updateSuggestions(null);
        });

        const firstLevel: HTMLElement = this.querySelector<HTMLElement>('#menu-first-level');
        if (firstLevel) {
            firstLevel.addEventListener('togglable-open', this.toggleFirstLevel);
            firstLevel.addEventListener('togglable-close', this.toggleFirstLevel);
            firstLevel.addEventListener('transitionend', this.toggleTransition);
            this.firstLevel = firstLevel;
            this.firstLevel.setAttribute('aria-hidden', (this.mobileMQ.matches).toString())
        }
        const mobileMenuToggle: HTMLElement = this.querySelector<HTMLElement>('#mobile-menu-toggle');
        if (mobileMenuToggle) {
            mobileMenuToggle.addEventListener(TriggerEvent.Click, this.toggleMobileMenu);
            mobileMenuToggle.addEventListener('togglable-open', this.toggleFirstLevel);
            mobileMenuToggle.addEventListener('togglable-close', this.toggleFirstLevel);
            this.mobileMenuToggle = mobileMenuToggle;
        }
        this.updateOpenStatus();

        addDelegatedEventListener(this, 'click', '.sm-collapsed', async () => {
            const cl = this.classList;
            const root: HTMLElement = document.querySelector(':root');
            if (cl.contains('opened')) {
                cl.remove('opened');
                root.style.setProperty('--header-height', '64px');
            } else {
                cl.add('opened');
                root.style.setProperty('--header-height', '92px');
            }
        });

        const search: HTMLElement = this.querySelector('.menu-search > a');
        if (search) {
            search.addEventListener('togglable-open', this.toggleSearch);
            search.addEventListener('togglable-close', this.toggleSearch);
            this.search = search;
        }

        const searchClient = (await (window['globalInjector'] as Promise<InversifyInjector>)).resolve<SearchClient>('searchClient');

        addDelegatedEventListener(this, 'click', '.search-suggestion', async (e: InputEvent) => {
            const value = (<HTMLElement>e.target).closest('.search-suggestion').textContent;
            const searchInput = this.querySelector<HTMLInputElement>('#search__input');
            const triggerValue = searchInput.value;
            searchInput.value = value;
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: 'click_suggested_keyword',
                eventLabel: value || 'null',
                keyword: triggerValue || 'null'
            });
            this.submitForm(searchInput);
        });
        addDelegatedEventListener(this, 'input', '#search__input', debounce(250, async (e: InputEvent & { target: HTMLInputElement }) => {
            const value = e.target.value;
            let suggested: string[] | null;
            if (value.length > 2) {
                const options = await searchClient.autocomplete({
                    query: value,
                    site: this.runtimeModel.siteKey
                });
                suggested = options?.map((suggestion) => {
                    return suggestion._source?.title;
                });
            } else {
                suggested = null;
            }
            if (this.suggestionRuntimeViewModel) {
                this.suggestionRuntimeViewModel.suggested(suggested || []);
                this.updateSuggestions(suggested);
            }
        }));
        addDelegatedEventListener(this, 'keydown', '#search__input', async (e: KeyboardEvent) => {
            const input = <HTMLInputElement>e.target;
            if (e.key === "Enter") {
                e.preventDefault();
                this.submitForm(input);
            }
        });
        addDelegatedEventListener(this, 'submit', 'form', async (e: KeyboardEvent) => {
            const form = <HTMLFormElement>e.target;
            const input = <HTMLInputElement>form.querySelector('#search__input');
            this.submitForm(input);
        });
        addDelegatedEventListener(this, 'click', '.menu__popular-card', async (e: Event) => {
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: 'click_most_popular_search',
                eventLabel: (<HTMLElement>e.target).closest('.menu__popular-card').querySelector('p').textContent || 'null'
            });
        });
        addDelegatedEventListener(this, 'click', '.menu__popular-card', async (e: Event) => {
            window.dataLayer.push({
                event: 'internal_search',
                eventCategory: 'site_search',
                eventAction: 'click_most_popular_search',
                eventLabel: (<HTMLElement>e.target).closest('.menu__popular-card').querySelector('p').textContent || 'null'
            });
        });
        addDelegatedEventListener(this, 'click', '.nav-item.first-level > button', async (e: Event) => {
            window.dataLayer.push({
                event: 'click_menu_item',
                eventCategory: 'menu_item_click',
                eventAction: (<HTMLElement>e.target).closest('.nav-item.first-level > button').textContent.trim(),
                eventLabel: 'not_present'
            })
        });
        addDelegatedEventListener(this, 'keydown', '#menu-first-level', this.keyboardMainNavigation)
        addDelegatedEventListener(this, 'keydown', '.header-top', this.keyboardLanguageDesktopNavigation)
    }

    private updateSuggestions(suggested: unknown[]): void {
        const highlights = this.querySelector<HTMLElement>('.search-highlights');
        if (suggested === null) {
            highlights?.classList.remove('hidden');
        } else if (suggested?.length >= 0) {
            highlights?.classList.add('hidden');
        }
        for (const w of this.suggestionWrapperElements()) {
            if (w !== null) {
                if (!ko.contextFor(w)) {
                    ko.applyBindings(this.suggestionRuntimeViewModel, w);
                }
            }
        }
    }

    public disconnectedCallback(): void {
        const mobileMenuToggle: HTMLElement = this.mobileMenuToggle;
        if (mobileMenuToggle) {
            mobileMenuToggle.removeEventListener(TriggerEvent.Click, this.toggleMobileMenu);
            mobileMenuToggle.removeEventListener('togglable-open', this.toggleFirstLevel);
            mobileMenuToggle.removeEventListener('togglable-close', this.toggleFirstLevel);
        }
        const firstLevel: HTMLElement = this.firstLevel;
        if (firstLevel) {
            firstLevel.removeEventListener('transitionend', this.toggleTransition);
            firstLevel.removeEventListener('togglable-open', this.toggleFirstLevel);
            firstLevel.removeEventListener('togglable-close', this.toggleFirstLevel);
        }
        // this.removeEventListener(TriggerEvent.Click, onClick); // Not really necessary since on 'this'

        const search: HTMLElement = this.search;
        if (search) {
            search.removeEventListener('togglable-open', this.toggleSearch);
            search.removeEventListener('togglable-close', this.toggleSearch);
        }
    }
}
